import { create } from "zustand";
import emitter from "../lib/emitter";

import { assetList } from "@/common/assets";
import { forwardRef, memo, useEffect, useState } from "react";
import { events } from "../common/events";
import { useGlobalStore } from "./global";

export const useAssetsStore = create((set, get) => ({
  assets: {},
  convertedAssets: {},
  loadedAssets: 0,
  totalAssets: assetList.length,
  addAsset: ({ type, name, value }) => {
    // Avoid object spread for better performance
    set((state) => {
      if (!state.assets[type]) {
        state.assets[type] = new Map();
      }

      if (state.assets[type].has(name)) {
        console.log("DUPLICATE", name);
        return state;
      }

      state.assets[type].set(name, value);
      state.loadedAssets++;

      emitter.emit(events.LOADING_PROGRESS, {
        loadedAssets: state.loadedAssets,
        progress: (state.loadedAssets / state.totalAssets) * 100,
      });

      if (state.loadedAssets >= state.totalAssets) {
        emitter.emit(events.LOADING_COMPLETE);
      }

      return { assets: state.assets, loadedAssets: state.loadedAssets };
    });
  },
  getProgress: () => {
    return (get().loadedAssets / get().totalAssets) * 100;
  },
}));

// TODO: Make pattern a prop

export const withAssets = (assetQueries, displayName) => (WrappedComponent) => {
  const AssetLoader = forwardRef((props, ref) => {
    const [assets, setAssets] = useState({});
    const noWebGLImages = useGlobalStore((s) => s.noWebGLImages);
    const [loading, setLoading] = useState(!noWebGLImages);

    useEffect(() => {
      if (noWebGLImages) return;

      const initialState = useAssetsStore.getState();
      let unsubscribe;
      const checkAndSetAssets = (state) => {
        const allQueriesSatisfied = assetQueries.every((query) => {
          const type = query.type || "image";
          const matchingAssets = Array.from(
            (state.assets?.[type] && state.assets[type].entries()) || []
          ).filter(([key]) => key.includes(query.pattern));

          return matchingAssets.length >= (query.expectedCount || 0);
        });

        if (allQueriesSatisfied) {
          const nextAssets = assetQueries.reduce((acc, query) => {
            const type = query.type || "image";
            const stateAssetsByType = state.assets?.[type] || new Map();
            const matchingAssets = Array.from(stateAssetsByType.entries())
              .filter(([key]) => key.includes(query.pattern))
              .map(([key, asset]) => ({ name: key, asset }))
              .sort((a, b) => {
                const numA = parseInt(a.name.match(/\d+/)?.[0] || "0");
                const numB = parseInt(b.name.match(/\d+/)?.[0] || "0");
                return numA - numB;
              });

            acc[query.groupName || query.pattern] = matchingAssets;
            return acc;
          }, {});

          const currKeys = Object.keys(assets);
          const nextKeys = Object.keys(nextAssets);
          const sameKeys =
            nextKeys.length === currKeys.length &&
            nextKeys.every((k) => currKeys.includes(k));
          const sameLengths = sameKeys
            ? nextKeys.every((k) => nextAssets[k].length === assets[k]?.length)
            : false;

          if (!sameKeys || !sameLengths) {
            setAssets(nextAssets);
            setLoading(false);
          }
          if (unsubscribe) {
            unsubscribe();
            unsubscribe = null;
          }
        }
      };

      // Check initial state
      checkAndSetAssets(initialState);

      // Subscribe to future changes
      unsubscribe = useAssetsStore.subscribe(checkAndSetAssets);

      return () => {
        if (unsubscribe) unsubscribe();
      };
    }, [assetQueries, noWebGLImages]);

    if (loading) {
      return null; // Replace with a loading UI if needed
    }

    // Forward props and ref
    return <WrappedComponent ref={ref} {...props} {...assets} />;
  });

  AssetLoader.displayName = `WithAssets(${
    displayName ||
    WrappedComponent.displayName ||
    WrappedComponent.name ||
    "Component"
  })`;

  return memo(AssetLoader);
};
